import { useCallback, useRef } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { AttentionIcon } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import {
  FadeOverflow,
  hiddenScrollbarCss,
  InfoCircleIcon,
} from '@root/shared/ui-kit'
import { FadeOverflowVirtuozo } from '@root/shared/ui-kit/utils/fade-overflow-virtuozo'

import {
  PairExploreItem,
  TradingPairExploreItemSkeleton,
} from '../pair-explore-item'

import { ExplorePairsEmpty } from './explore-pairs-empty'

import type {
  PairMetrics,
  PairModel,
  PriceChartInterval,
} from '@root/entities/pair'
import type { Error } from '@root/shared'
import type { ClassNameProps } from '@root/shared/react'

type ExploreBlockSectionProps = {
  pairsState: AsyncStateN.AsyncStateN<Error.UnknownError, Array<PairModel>>
  onRetry: () => void
  onItemClick?: (options: { pair: PairModel }) => void
  offsetBottom?: number | string | undefined
  statsPeriod: PairMetrics.MetricsSpan
  priceChartInterval: PriceChartInterval
  listRef?: React.Ref<HTMLElement>
} & ClassNameProps

export const ExploreList: React.FC<ExploreBlockSectionProps> = ({
  pairsState,
  onRetry,
  offsetBottom,
  onItemClick,
  statsPeriod,
  priceChartInterval,
  listRef,
}) => {
  const { t } = useTranslation()

  const listElementRef = useRef<HTMLElement>(null)
  // Stable ref callback: inline + useCallback so the `<List>` ref keeps a
  // stable identity across re-renders. A fresh callback every render would
  // invoke ref(null) → ref(el) on every realtime pair update and thrash any
  // downstream effects that observe the element.
  const mergedListRef = useCallback(
    (node: HTMLElement | Window | null) => {
      if (!(node instanceof HTMLElement)) {
        return
      }
      listElementRef.current = node
      assignExternalRef(listRef, node)
    },
    [listRef],
  )
  return AsyncStateN.matchWaiting(pairsState, {
    Waiting: () => (
      <List
        ref={mergedListRef}
        variant="vertical"
        mode="always"
        key="skeletons"
      >
        <TradingPairExploreItemSkeleton />
        <TradingPairExploreItemSkeleton />
        <TradingPairExploreItemSkeleton />
        <TradingPairExploreItemSkeleton />
      </List>
    ),
    Error: () => (
      <ExplorePairsEmpty
        icon={<AttentionIcon size={24} />}
        title={t('common.loading-error')}
        retryText={t('common.retry')}
        description={t('common.something-went-wrong')}
        onRetry={onRetry}
      />
    ),
    Success: (pairs) =>
      pairs.length === 0 ? (
        <ExplorePairsEmpty
          icon={<InfoCircleIcon size={20} />}
          title={t('common.empty-unfortunately')}
          description={t('common.no-items-found')}
          retryText={t('common.refresh')}
          onRetry={onRetry}
        />
      ) : (
        <ScrollVirtuozo
          scrollerRef={mergedListRef}
          data={pairs}
          computeItemKey={(_, pair) => pair.address}
          itemContent={(index, pair) => (
            <ListItem
              key={pair.address}
              style={{
                paddingBottom:
                  index === pairs.length - 1 ? offsetBottom : undefined,
              }}
            >
              <PairExploreItem
                key={pair.address}
                onClick={onItemClick}
                pair={pair}
                statsPeriod={statsPeriod}
                priceChartInterval={priceChartInterval}
                withHide
              />
            </ListItem>
          )}
        />
      ),
  })
}

/**
 * Forwards a node to an external ref (callback or object). Kept as a module-
 * level helper so the React Compiler doesn't flag the `.current` write as a
 * prop mutation inside the component body.
 */
const assignExternalRef = <T,>(
  ref: React.Ref<T> | undefined,
  node: T | null,
): void => {
  if (typeof ref === 'function') {
    ref(node)
    return
  }
  if (ref) {
    ;(ref as React.MutableRefObject<T | null>).current = node
  }
}

const ListItem = styled.div`
  padding: 3px 8px;
`

const List = styled(FadeOverflow)`
  overflow-y: auto;
  max-height: 100%;
  display: grid;
  align-self: start;
  gap: 6px;
  padding-inline: 12px;
  padding-block: 12px;

  ${hiddenScrollbarCss}
`

const ScrollVirtuozo = styled(FadeOverflowVirtuozo<PairModel, never>)`
  ${hiddenScrollbarCss}
`
